import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, useTooltip } from '@frontend/design-system';

interface ChipListItem {
  name: string;
}

type ChipsListProps<T extends ChipListItem> = {
  items?: T[];
  maxVisible?: number;
  renderChip: (item: T) => React.ReactNode;
};

export const ChipsList = <T extends ChipListItem>({ items = [], maxVisible = 2, renderChip }: ChipsListProps<T>) => {
  const { t } = useTranslation('schedule-settings');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  const { visibleItems, hiddenItems } = useMemo(() => {
    const visibleItemList: T[] = [];
    const hiddenItemList: T[] = [];

    items
      .filter((item, index, self) => item.name && self.findIndex((content) => content.name === item.name) === index) // Remove duplicates & empty names
      .forEach((item, index, arr) => {
        if (index + 1 < maxVisible || arr.length === maxVisible) {
          visibleItemList.push(item);
        } else {
          hiddenItemList.push(item);
        }
      });

    return { visibleItems: visibleItemList, hiddenItems: hiddenItemList };
  }, [items, maxVisible]);

  if (!visibleItems.length) {
    return null;
  }

  const hiddenItemsText = hiddenItems.map((item) => item.name).join(', ');

  return (
    <section css={containerStyles}>
      {visibleItems.map((item) => renderChip(item))}
      {hiddenItems.length > 0 && (
        <>
          <Chip variant='neutral' {...triggerProps}>
            +{hiddenItems.length} {t('more')}
          </Chip>
          <Tooltip {...tooltipProps}>{hiddenItemsText}</Tooltip>
        </>
      )}
    </section>
  );
};

const containerStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  overflow: hidden;
`;
